import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Box, Group, Stack, Blockquote } from '@mantine/core'
import { AppBadge, AppButton, AppModal, AppSkeleton, AppText, AppTextarea } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { EmptyState, AmmoScoreBadge, SidebarLayout } from '../../app/common/ui'
import { FlagTargetType } from '../../app/models/flag'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'

const TARGET_TYPE_LABEL: Record<number, string> = {
  [FlagTargetType.Post]: 'Post',
  [FlagTargetType.Comment]: 'Comment',
  [FlagTargetType.Part]: 'Part',
  [FlagTargetType.FirearmModel]: 'Firearm model',
  [FlagTargetType.Build]: 'Build',
  [FlagTargetType.SlopPost]: 'Slop post'
}

const TARGET_TYPE_COLOR: Record<number, string> = {
  [FlagTargetType.Post]: 'blue',
  [FlagTargetType.Comment]: 'orange',
  [FlagTargetType.Part]: 'green',
  [FlagTargetType.FirearmModel]: 'navy',
  [FlagTargetType.Build]: 'gray',
  [FlagTargetType.SlopPost]: 'pink'
}

function truncate (s: string, len: number) {
  if (!s) return '—'
  return s.length <= len ? s : s.slice(0, len) + '…'
}

function EscalationList () {
  const { moderationStore } = useStore()
  const { escalations, loadingEscalations, selectedEscalation, selectEscalation } = moderationStore

  if (loadingEscalations && escalations.length === 0) {
    return (
      <Stack>
        {[1, 2, 3, 4, 5].map((i) => (
          <AppSkeleton key={i} height={72} radius="sm" />
        ))}
      </Stack>
    )
  }

  if (escalations.length === 0) {
    return <EmptyState title="No escalations" message="No flags are currently escalated." />
  }

  return (
    <Stack spacing="xs">
      {escalations.map((flag) => (
        <Box
          key={flag.id}
          onClick={() => selectEscalation(flag)}
          sx={{
            padding: 12,
            borderRadius: 8,
            border: selectedEscalation?.id === flag.id ? '2px solid var(--mantine-color-blue-5)' : '1px solid var(--mantine-color-gray-3)',
            backgroundColor: selectedEscalation?.id === flag.id ? 'var(--mantine-color-blue-0)' : undefined,
            cursor: 'pointer'
          }}
        >
          <Group spacing="xs" mb={4}>
            <AppBadge color={TARGET_TYPE_COLOR[flag.targetType]} size="sm">
              {TARGET_TYPE_LABEL[flag.targetType]}
            </AppBadge>
            <AppText size="sm" weight={500}>
              {truncate(flag.targetTitle, 40)}
            </AppText>
          </Group>
          <AppText size="xs" color="dimmed">
            Escalated by @{flag.reviewedByUsername ?? '—'} · {flag.reviewedAt ? formatDistanceToNow(new Date(flag.reviewedAt), { addSuffix: true }) : '—'}
          </AppText>
          <AppText size="xs" color="dimmed" mt={4}>
            {truncate(flag.resolution ?? '', 60)}
          </AppText>
        </Box>
      ))}
    </Stack>
  )
}

function EscalationResolutionPane () {
  const { moderationStore } = useStore()
  const { selectedEscalation, resolveEscalation, loadEscalations, submitting } = moderationStore
  const [modalAction, setModalAction] = useState<'AdminResolve' | 'ReturnToQueue' | null>(null)
  const [notes, setNotes] = useState('')

  const handleConfirm = async () => {
    if (!selectedEscalation || !modalAction || notes.trim().length < 10) return
    try {
      await resolveEscalation(selectedEscalation.id, modalAction, notes.trim())
      moderationStore.selectEscalation(null)
      loadEscalations()
      if (modalAction === 'AdminResolve') toast.error('Escalation resolved')
      else toast.warning('Flag returned to queue')
    } catch {
      // interceptor
    } finally {
      setModalAction(null)
      setNotes('')
    }
  }

  if (!selectedEscalation) {
    return (
      <EmptyState
        title="Select an escalation"
        message="Choose an escalation from the list to resolve it here."
      />
    )
  }

  const f = selectedEscalation

  return (
    <Stack spacing="lg">
      <Box>
        <Group spacing="xs" mb="xs">
          <AppBadge color={TARGET_TYPE_COLOR[f.targetType]} size="sm">
            {TARGET_TYPE_LABEL[f.targetType]}
          </AppBadge>
          <AppText weight={600}>{f.targetTitle || '—'}</AppText>
        </Group>
        <Box sx={{ padding: 12, borderRadius: 8, border: '1px solid var(--mantine-color-gray-3)', backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <AppText size="xs" color="dimmed">
            @{f.targetOwnerUsername}
          </AppText>
          <Group spacing="xs" mt={4}>
            <AmmoScoreBadge tier={f.targetOwnerTier} score={f.targetOwnerAmmoScore ?? 0} size="sm" />
            <AppText size="xs">{f.targetOwnerFlagsAgainst} previous flags on their content</AppText>
          </Group>
        </Box>
        <Blockquote mt="sm" cite={null} styles={{ cite: { display: 'none' } }}>
          <AppText size="sm">{f.reason}</AppText>
        </Blockquote>
        {f.resolution && (
          <Box mt="sm" sx={{ padding: 12, borderRadius: 8, backgroundColor: 'var(--mantine-color-yellow-0)', border: '1px solid var(--mantine-color-yellow-3)' }}>
            <AppText size="xs" weight={600} mb={4}>
              Escalation notes
            </AppText>
            <AppText size="sm">{f.resolution}</AppText>
          </Box>
        )}
      </Box>

      <Box>
        <AppText size="sm" weight={600} mb="xs">
          Reporter: @{f.reporterUsername}
        </AppText>
      </Box>

      <Group spacing="sm">
        <AppButton
          variant="light"
          color="red"
          onClick={() => { setModalAction('AdminResolve'); setNotes('') }}
        >
          Admin Resolve
        </AppButton>
        <AppButton
          variant="light"
          color="yellow"
          onClick={() => { setModalAction('ReturnToQueue'); setNotes('') }}
        >
          Return to Queue
        </AppButton>
      </Group>

      <AppModal
        opened={!!modalAction}
        onClose={() => { setModalAction(null); setNotes('') }}
        title={modalAction === 'AdminResolve' ? 'Admin Resolve' : 'Return to Queue'}
      >
        <Stack>
          <AppText size="sm" color="dimmed">
            {modalAction === 'AdminResolve'
              ? "Permanently resolve this escalation. Content owner's AmmoScore will be affected."
              : 'Return this flag to the moderator queue for re-review.'}
          </AppText>
          <AppTextarea
            label="Notes"
            placeholder={modalAction === 'AdminResolve' ? 'Required resolution notes (min 10 characters)...' : 'Required notes...'}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group position="right">
            <AppButton variant="default" onClick={() => { setModalAction(null); setNotes('') }}>
              Cancel
            </AppButton>
            <AppButton
              color={modalAction === 'AdminResolve' ? 'red' : 'yellow'}
              onClick={handleConfirm}
              loading={submitting}
              disabled={notes.trim().length < 10}
            >
              Confirm
            </AppButton>
          </Group>
        </Stack>
      </AppModal>
    </Stack>
  )
}

export default observer(function EscalationsTab () {
  return (
    <SidebarLayout
      main={<EscalationList />}
      sidebar={<EscalationResolutionPane />}
      sidebarWidth={440}
    />
  )
})
